import { ProductType } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { isMarketFulfilledProduct } from 'shared/custom/utils/inventoryUtils';

export const getIndexedQuantity = (
	product: ProductType | undefined,
	locationId: string | undefined
): number | undefined => {
	if (!product || !locationId) return undefined;
	const raw = product[`inventories.${locationId}.quantity`];
	if (raw === undefined || raw === null || raw === '') return undefined;
	const quantity = Number(raw);
	return Number.isNaN(quantity) ? undefined : quantity;
};

export type PickupAvailability = {
	/** Inventory in the selected store, `undefined` when the index omits the key. */
	storeAvailability: number | undefined;
	/** Inventory across the selected store's market, `undefined` when the index omits the key. */
	marketAvailability: number | undefined;
};

/**
 * The store level is keyed by the physical store's `uniqueId` (e.g. `12568`), the
 * market level by `M<marketId>` — and `marketId` is only reachable from the
 * `physicalStoreId`/`stLocId` store number (e.g. `8652`), so both are needed.
 */
export type PickupLocationIds = { storeUniqueId: string; physicalStoreId: string };

const getMarketLocationId = (physicalStoreId: string): string | undefined => {
	const marketId = physicalStoreId
		? getStoreDataByPhysicalStoreId(physicalStoreId)?.marketId
		: undefined;
	return marketId ? `M${marketId}` : undefined;
};

/** Inventory across a store's market, `undefined` when the index omits the key. */
export const getMarketAvailability = (
	product: ProductType | undefined,
	physicalStoreId: string
): number | undefined => getIndexedQuantity(product, getMarketLocationId(physicalStoreId));

export const getPickupAvailability = (
	product: ProductType | undefined,
	{ storeUniqueId, physicalStoreId }: PickupLocationIds
): PickupAvailability => ({
	storeAvailability: getIndexedQuantity(product, storeUniqueId),
	marketAvailability: getMarketAvailability(product, physicalStoreId),
});

/** Whether a combo can be assembled in the selected store, in its market, or not at all. */
export type ComboStock = 'all-in-store' | 'all-in-market' | 'incomplete';

/**
 * Combo (TYPES.kit) pickup stock, aggregated over the components — a combo is only
 * pickable where every one of its components is. Each component's indexed quantity
 * is divided by the units the combo consumes, so a component stocked below its
 * required quantity does not count as available.
 *
 * A component whose inventory key the index omits counts as available, matching how
 * `usePickupDate` treats an unknown level for a standalone SKU.
 */
export const getComboStock = (
	components: ProductType[],
	{ storeUniqueId, physicalStoreId }: PickupLocationIds
): ComboStock => {
	if (components.length === 0) return 'incomplete';

	const isBuildable = (component: ProductType, locationId: string | undefined): boolean => {
		const quantity = getIndexedQuantity(component, locationId);
		if (quantity === undefined) return true;
		const required = Number(component.quantity) || 1;
		return Math.floor(quantity / required) > 0;
	};

	if (components.every((component) => isBuildable(component, storeUniqueId))) {
		return 'all-in-store';
	}

	const marketLocationId = getMarketLocationId(physicalStoreId);
	if (
		components.every(
			(component) => isMarketFulfilledProduct(component) && isBuildable(component, marketLocationId)
		)
	) {
		return 'all-in-market';
	}
	return 'incomplete';
};

// --- PDP delivery-options story (AC3, AC5, AC6) -----------------------------
//
// The PLP functions above source availability from the search index, which may
// omit a level entirely — hence their `undefined` ("unknown, assume available")
// escape hatch. The PDP instead consumes the inventory availability call the page
// already makes, and AC3 explicitly places the guarantee that this response
// always carries inventory on the platform, not on this resolution. So unlike
// `getIndexedQuantity`, an absent or unparsable PDP value defaults to zero rather
// than to "assume available".

export type PdpChannelAvailability = {
	/** AC5: physical inventory of the selected store — the store-pickup counter. */
	storeAvailability: number;
	/** AC5/AC6: market-level inventory — the home-delivery counter, and the
	 * store-pickup counter's substitute under the BOSS exception. */
	marketAvailability: number;
};

const parseAvailabilityCount = (value: string | undefined): number => {
	const parsed = Number.parseInt(value ?? '', 10);
	return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
};

/**
 * AC3: the two counters come from the same inventory availability response —
 * `availableQuantity` for the store, `x_AvailableQuantityInMarket` for the market,
 * falling back to `x_customField2` when the market field does not arrive. This is
 * the same fallback the PDP's own on-page availability copy already applies (see
 * `MarketInventory.tsx` / `getAvailabilityState`), reused here rather than
 * reimplemented.
 *
 * `record` is expected to already be combo-normalized when the product is a kit —
 * `getStoreAvailabilityKit` (presentation/components/.../ProductDetails/utils/utils.ts)
 * aggregates every component's availability into a single per-kit-unit count
 * before this function ever sees it, so a combo and a standalone SKU are read
 * identically here.
 */
export const getPdpChannelAvailability = (
	record: ProductAvailabilityData | undefined
): PdpChannelAvailability => ({
	storeAvailability: parseAvailabilityCount(record?.availableQuantity),
	marketAvailability:
		parseAvailabilityCount(record?.x_AvailableQuantityInMarket) ||
		parseAvailabilityCount(record?.x_customField2),
});
